import { useEffect, useState, type FormEvent } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, ArrowRight } from 'lucide-react';
import { AppHeader } from '@/components/AppHeader';
import { Field } from '@/components/ui/Field';
import { useAuth } from '@/lib/auth-store';
import { api, ApiError } from '@/lib/api';

/**
 * Student-facing self-enroll. Two entry shapes:
 *   - `/join/:joinCode` — code in the URL (deep link); we attempt the join
 *     immediately on mount.
 *   - `/join`           — empty form, the student types the code.
 *
 * On success we route to the course detail page. AlreadyEnrolledError → still
 * route there (idempotent UX). InvalidCode → render the form with an error.
 */
export function JoinCourse() {
  const { state } = useAuth();
  const { joinCode: codeFromUrl } = useParams();
  const navigate = useNavigate();
  const [code, setCode] = useState((codeFromUrl ?? '').toUpperCase());
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // If a code came in via the URL, attempt the join immediately.
  useEffect(() => {
    if (state.status !== 'authenticated') return;
    if (!codeFromUrl) return;
    void attemptJoin(codeFromUrl.toUpperCase(), state.token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeFromUrl, state.status]);

  if (state.status === 'anonymous') {
    return (
      <Navigate
        to={`/auth/sign-in?next=${encodeURIComponent('/join' + (codeFromUrl ? `/${codeFromUrl}` : ''))}`}
        replace
      />
    );
  }

  if (state.status !== 'authenticated') return null;

  async function attemptJoin(joinCode: string, token: string) {
    setError(null);
    setSubmitting(true);
    try {
      const { course } = await api.courses.join(token, { joinCode });
      // Land directly inside the course they just joined. Workspace was
      // wrong for students — that page is instructor-language ("Where you
      // build, run, and iterate on your courses") and shows their just-
      // joined class with a DRAFT pill + the join code. The course detail
      // page is what they actually want to see.
      navigate(`/c/${course.orgSlug}/${course.slug}`, { replace: true });
    } catch (e) {
      if (e instanceof ApiError && e.code === 'join_code_invalid') {
        setError("That code didn't match any course. Check and try again.");
      } else if (e instanceof ApiError && e.code === 'already_enrolled') {
        // Already enrolled — send them to the dashboard (their classes list).
        navigate('/dashboard');
        return;
      } else {
        setError("EduBorg couldn't process that just now. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (state.status !== 'authenticated') return;
    if (code.trim().length < 4) {
      setError('Codes are at least 4 characters.');
      return;
    }
    await attemptJoin(code.trim().toUpperCase(), state.token);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[hsl(220,40%,98%)] via-background to-secondary/40">
      <AppHeader />

      <main className="container max-w-md py-16">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="rounded-3xl border border-border bg-card p-8 shadow-lg"
        >
          <header className="mb-6 text-center">
            <img
              src="/brand/eduborg-mascot.png"
              alt=""
              aria-hidden
              width={64}
              height={64}
              className="mx-auto animate-mascot-idle"
            />
            <h1 className="mt-4 font-display text-2xl font-bold tracking-tight">Join a course</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Type the 6-character code your teacher gave you.
            </p>
          </header>

          <form onSubmit={onSubmit} className="space-y-4">
            <Field
              label="Course code"
              autoFocus
              required
              maxLength={12}
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              hint="e.g. K7M9P2"
              className="text-center font-mono text-lg uppercase tracking-[0.4em]"
            />
            {error && (
              <div
                role="alert"
                className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </div>
            )}
            <button
              type="submit"
              disabled={submitting || code.length < 4}
              className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-5 py-3 text-sm font-semibold text-primary-foreground shadow-md transition-all hover:translate-y-[-1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-70"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
              {submitting ? 'Joining…' : 'Join course'}
              {!submitting && <ArrowRight className="h-4 w-4" aria-hidden />}
            </button>
          </form>
        </motion.section>
      </main>
    </div>
  );
}
